/**
 * Contrôleur de l'application : une seule machine à états (plan W06), boucle à tick fixe + interpolation,
 * cycle des missions et télémétrie. Seul endroit qui relie simulation, rendu, entrées, HUD, audio et menus.
 *
 * Monde persistant (phase C) : on part amarré à un dock (station ou porteur). À quai, le menu du dock s'ouvre
 * en jeu (missions, armurerie, hangar à la station) pendant que la simulation continue ; une mission choisie
 * commence au décollage.
 *
 * Télémétrie v2 : une session = une partie (de l'entrée dans le monde à la fermeture), enregistrée par
 * `SessionRecorder` et envoyée par points de contrôle ; les missions y sont des entrées détaillées.
 */
import { Engine, TargetCamera, Vector3 } from '@babylonjs/core';
import { AudioSystem } from './audio/audio';
import { dataHash, isAtmosphericJump, jumpTargets, parseGameData, shipIds, slotIds, zoneOf, type DockId, type GameData, type ShipId, type SlotId } from './data';
import { InputManager, type UiAction } from './input/input';
import { awardSortie, buyItem, equipItem, loadProfile, owns, saveProfile, type PilotProfile, type Reward } from './progression/profile';
import { FIGHTER_JUMP, carrierAtmosphereProfile, carrierJumpProfile } from './render/jumpFx';
import { slotAvailable } from './sim/loadout';
import { WorldRenderer } from './render/world';
import type { MissionMode } from './sim/encounter/mission';
import { Quat, Vec3 } from './sim/core/math';
import { BotPilot } from './sim/pilots/bot';
import { emptyInput } from './sim/pilots/player';
import type { SimEvent } from './sim/events';
import { Simulation, TICK_DT, createCollisionWorld } from './sim/simulation';
import { initPhysics, type CollisionWorld } from './sim/world/collision';
import { SESSION_SCHEMA, SessionRecorder, type GameSessionSummary, type MissionEntry } from './telemetry/session';
import { FpsMeter, download, flushPending, listSessions, machineInfo, saveSession, toJsonl } from './telemetry/store';
import { Hud } from './ui/hud';
import { Menus, dockTabs, type DockMission, type DockTab, type MenuAction } from './ui/menus';
import { loadSettings, saveSettings, type Settings } from './ui/settings';
import { Tutorial } from './ui/tutorial';

/** `jumping` : séquence de saut entre zones (simulation suspendue, caméra extérieure). */
export type AppState = 'loading' | 'pilot' | 'title' | 'playing' | 'docked' | 'jumping' | 'paused' | 'options' | 'help' | 'report' | 'results' | 'confirmQuit' | 'error';

/** Transitions autorisées : toute autre demande est ignorée (et signalée en dev). */
const TRANSITIONS: Record<AppState, AppState[]> = {
  loading: ['pilot', 'title', 'playing', 'docked', 'error'],
  pilot: ['title'],
  title: ['pilot', 'playing', 'docked', 'options', 'help'],
  playing: ['paused', 'results', 'docked', 'title', 'jumping'],
  docked: ['playing', 'options', 'help', 'report', 'results', 'confirmQuit', 'jumping'],
  jumping: ['docked', 'playing', 'title'],
  paused: ['playing', 'options', 'help', 'report', 'confirmQuit'],
  report: ['paused', 'docked'],
  options: ['title', 'paused', 'docked'],
  help: ['title', 'paused', 'docked'],
  confirmQuit: ['paused', 'docked', 'title'],
  results: ['playing', 'docked'],
  error: [],
};

const MAX_TICKS_PER_FRAME = 12;
const RESULTS_DELAY = 2.5;

/** Mission en cours (entrée de la session de télémétrie). */
interface MissionMeta {
  scenario: string;
  ship: ShipId;
  startT: number;
  saved: boolean;
}

/** Point de contrôle de la télémétrie : envoi régulier de la partie en cours (secondes réelles). */
const CHECKPOINT_INTERVAL = 60;

export class App {
  state: AppState = 'loading';
  private returnTo: AppState = 'title';
  data!: GameData;
  private engine!: Engine;
  renderer!: WorldRenderer;
  /** Mondes de collision par zone, construits à la première visite. */
  private readonly collisions = new Map<string, CollisionWorld>();
  sim: Simulation | null = null;
  private input!: InputManager;
  private hud!: Hud;
  private menus: Menus;
  private readonly audio = new AudioSystem();
  private settings: Settings = loadSettings();
  private tutorial: Tutorial | null = null;
  private seed = 0;
  private meta: MissionMeta | null = null;
  /** Dernière mission terminée (écran de résultats). */
  private lastMission: MissionEntry | null = null;
  /** Partie en cours d'enregistrement. */
  private recorder: SessionRecorder | null = null;
  private sessionStart = 0;
  private checkpointTimer = CHECKPOINT_INTERVAL;
  /** Erreurs survenues hors partie (titre, chargement), jointes à la prochaine session. */
  private pendingErrors: { message: string; detail: string }[] = [];
  private accumulator = 0;
  /** Temps de simulation de la fin de mission (écran de résultats après un court délai). */
  private endedAt: number | null = null;
  private fps = new FpsMeter();
  private autopilot: BotPilot | null = null;
  /**
   * Démarrage par l'URL (tests, captures) : catapulte automatique, pas de menu de dock, scénario imposé.
   */
  private scripted = false;
  /** Arrivée à quai pendant le tick : le menu du dock s'ouvre après la boucle. */
  private dockArrival = false;
  private debug = false;
  private menuCamera!: TargetCamera;
  /** Profil du pilote (phase C) : crédits, objets, chargements. */
  private profile!: PilotProfile;
  private dockTab: DockTab = 'missions';
  private armorySlot: SlotId = 'primary';
  private dockMessage = '';
  private lastReward: Reward | null = null;
  private statusTimer = 0;
  private menuTime = 0;
  private tuning: { update: () => void; dispose: () => void } | null = null;
  /** Micro-pause de la simulation après une destruction (impact ressenti). */
  private hitStop = 0;
  /** Départ en vol libre hors des docks (galerie, captures). */
  private freeStart = false;
  private wasBoosting = false;
  /** Saut de chasseur prêt (événement de la simulation), exécuté à l'image suivante. */
  private pendingJump: string | null = null;

  constructor(private readonly canvas: HTMLCanvasElement, private readonly hudCanvas: HTMLCanvasElement, menuRoot: HTMLElement) {
    this.menus = new Menus(menuRoot, () => this.data, (action, value) => this.onMenuAction(action, value));
  }

  async init(): Promise<void> {
    this.captureErrors();
    this.menus.show({ kind: 'loading', text: 'Chargement des réglages…' });
    try {
      this.data = parseGameData();
    } catch (error) {
      this.fail(String((error as Error).message));
      return;
    }
    this.profile = loadProfile(this.data);
    this.menus.show({ kind: 'loading', text: 'Initialisation du moteur…' });
    this.engine = new Engine(this.canvas, true, { stencil: true, preserveDrawingBuffer: true, antialias: true }, true);
    await initPhysics();
    this.renderer = new WorldRenderer(this.engine, this.data, this.settings);
    const missing = await this.renderer.load((done, total) => this.menus.show({ kind: 'loading', text: `Chargement des modèles ${done}/${total}…` }));
    if (missing.length > 0) console.info(`Modèles absents, silhouettes de repli utilisées : ${missing.join(', ')}`);
    this.menuCamera = new TargetCamera('menu', new Vector3(0, 120, 520), this.renderer.scene);
    this.menuCamera.fovMode = 1;
    this.menuCamera.fov = 1.1;
    this.menuCamera.minZ = 0.5;
    this.menuCamera.maxZ = 60000;
    this.renderer.jump.onCue = (cue) => (cue === 'open' ? this.audio.play('MissileLaunch', 0.9, 0.42) : this.audio.play('Explosion', 0.55, 1.6));
    this.input = new InputManager(this.canvas, this.settings);
    this.input.onUi((a) => this.onUi(a));
    this.hud = new Hud(this.hudCanvas, this.data, this.settings);
    this.audio.configure(this.settings.sound, this.settings.volume);
    window.addEventListener('resize', () => this.engine.resize());
    document.addEventListener('pointerlockchange', () => {
      if (!document.pointerLockElement && this.state === 'playing' && this.sim) this.pause();
    });
    // Fermeture : la mission en cours est abandonnée, la partie part une dernière fois.
    window.addEventListener('pagehide', () => this.closeSession('closed'));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.checkpoint();
    });
    document.addEventListener('pointerdown', () => void this.audio.unlock(), { capture: true });
    document.addEventListener('keydown', () => void this.audio.unlock(), { capture: true });
    if (this.settings.shareTelemetry) void flushPending();
    this.engine.runRenderLoop(() => this.frame());
    (window as unknown as { __spaceArena: App }).__spaceArena = this;

    const params = new URLSearchParams(location.search);
    const ship = params.get('ship') as ShipId | null;
    const mode = params.get('mode') as MissionMode | null;
    if (params.get('autostart') === '1') {
      if (params.get('skipTutorial') === '1') this.settings.tutorialDone = true;
      this.freeStart = params.get('free') === '1' || params.has('gallery');
      const style = params.get('livery');
      if (style === 'bands' || style === 'twoTone' || style === 'tinted' || style === 'original') {
        this.data.art.liveryStyle = style;
        this.renderer.applyLiveries();
      }
      if (params.get('autopilot') === '1') this.autopilot = new BotPilot();
      if (ship && shipIds.includes(ship)) this.settings.lastShip = ship;
      if (!this.settings.playerName) this.settings.playerName = this.autopilot ? 'pilote-auto' : 'test';
      const seed = params.has('seed') ? Number(params.get('seed')) : undefined;
      this.scripted = true;
      // Zone : paramètre `zone`, sinon celle du scénario demandé.
      const zone = params.get('zone') ?? this.data.missions.scenarios[mode ?? 'mission']?.zone ?? this.data.zones.home;
      this.startWorld(params.get('dock') === 'station' ? 'station' : 'carrier', mode ?? 'mission', seed, zone);
      if ((params.get('gallery') === '1' || params.get('gallery') === 'near') && this.sim) this.spawnGallery(this.sim);
    } else {
      this.go(this.settings.playerName ? 'title' : 'pilot');
    }
  }

  /** Erreurs JavaScript et console.error : jointes à la télémétrie pour les rapports de bug. */
  private captureErrors(): void {
    window.addEventListener('error', (e) => this.reportError(e.message || 'erreur', e.error instanceof Error ? e.error.stack ?? '' : `${e.filename}:${e.lineno}`));
    window.addEventListener('unhandledrejection', (e) => {
      const reason = e.reason as unknown;
      this.reportError(`promesse rejetée : ${reason instanceof Error ? reason.message : String(reason)}`, reason instanceof Error ? reason.stack ?? '' : '');
    });
    const original = console.error.bind(console);
    console.error = (...args: unknown[]) => {
      original(...args);
      this.reportError(args.map((a) => (a instanceof Error ? a.message : typeof a === 'string' ? a : JSON.stringify(a))).join(' '), args.find((a) => a instanceof Error)?.stack ?? '');
    };
  }

  private reportError(message: string, detail: string): void {
    if (this.recorder) this.recorder.error(message, detail);
    else if (this.pendingErrors.length < 20) this.pendingErrors.push({ message, detail });
  }

  private fail(text: string): void {
    this.state = 'error';
    this.menus.show({ kind: 'error', text });
  }

  private go(next: AppState): void {
    if (!TRANSITIONS[this.state].includes(next)) {
      if (import.meta.env.DEV) console.warn(`Transition refusée : ${this.state} → ${next}`);
      return;
    }
    const previous = this.state;
    this.state = next;
    if (next === 'options' || next === 'help' || next === 'confirmQuit' || next === 'report') this.returnTo = previous;
    if (this.recorder && next === 'paused' && previous === 'playing') this.recorder.appEvent('pause');
    if (this.recorder && next === 'playing' && previous === 'paused') this.recorder.appEvent('resume');
    this.input.flightActive = next === 'playing';
    if (next !== 'playing') {
      this.input.releaseAll();
      // Saut de chasseur : la souris reste capturée pour reprendre le vol sans clic.
      if (next !== 'jumping') this.input.exitPointerLock();
    }
    this.renderer.scene.activeCamera = this.inMenuScene() ? this.menuCamera : this.renderer.rig.camera;
    switch (next) {
      case 'title': this.menus.show({ kind: 'title', player: this.settings.playerName }); break;
      case 'pilot': this.menus.show({ kind: 'pilot', name: this.settings.playerName, first: !this.settings.playerName }); break;
      case 'report': this.menus.show({ kind: 'report' }); break;
      case 'docked': this.showDock(); break;
      case 'paused': this.menus.show({ kind: 'paused' }); break;
      case 'options': this.menus.show({ kind: 'options', settings: this.settings }); break;
      case 'help': this.menus.show({ kind: 'help', keys: this.keyLabels() }); break;
      case 'confirmQuit': this.menus.show({ kind: 'confirmQuit', missionActive: !!this.sim?.mission.active }); break;
      case 'results':
        if (this.lastMission) {
          const m = this.lastMission;
          this.menus.show({ kind: 'results', summary: m.summary, ship: m.ship as ShipId, mode: m.scenario, reward: this.lastReward, credits: this.profile.credits });
        }
        break;
      case 'playing':
        this.menus.hide();
        this.input.requestPointerLock();
        break;
      case 'jumping':
        this.menus.hide();
        break;
      default: break;
    }
    if (next === 'playing') this.audio.startEngine();
    else this.audio.updateEngine(null);
  }

  /** Menus hors partie : décor animé. */
  private inMenuScene(): boolean {
    return this.state === 'title' || this.state === 'pilot' || ((this.state === 'options' || this.state === 'help') && !this.sim);
  }

  private keyLabels(): Record<string, string> {
    const keys = ['forward', 'backward', 'left', 'right', 'up', 'down', 'rollLeft', 'rollRight', 'boost', 'brake', 'throttleZero', 'jump'] as const;
    return Object.fromEntries(keys.map((k) => [k, this.input.bindingLabel(k)]));
  }

  // --- Monde et missions -----------------------------------------------------

  private collisionFor(zone: string): CollisionWorld {
    let world = this.collisions.get(zone);
    if (!world) {
      world = createCollisionWorld(this.data, zone);
      this.collisions.set(zone, world);
    }
    return world;
  }

  /** Entre dans le monde, amarré à un dock ; `mode` impose un scénario dès le départ (démarrage par l'URL). */
  startWorld(dock: DockId, mode: MissionMode = 'sandbox', seed = Math.floor(Math.random() * 2 ** 31), zone = this.data.zones.home): void {
    this.closeSession('quit');
    const ship = this.settings.lastShip;
    this.seed = seed;
    zone = zoneOf(this.data, zone).id;
    this.sim = new Simulation(this.data, {
      shipId: ship,
      mode,
      seed,
      mouseMode: this.settings.mouseMode,
      startDocked: !this.freeStart,
      startDock: dock,
      autoLaunch: this.scripted,
      loadout: this.profile.loadouts[ship],
      zone,
    }, this.collisionFor(zone));
    this.meta = null;
    this.lastMission = null;
    this.lastReward = null;
    this.endedAt = null;
    this.dockArrival = false;
    this.dockTab = 'missions';
    this.dockMessage = '';
    this.renderer.attach(this.sim);
    // Le HUD lit les réglages équipés de la session (portée du radar…).
    this.hud.setData(this.sim.state.data);
    this.hud.reset();
    this.fps = new FpsMeter();
    this.accumulator = 0;
    this.tutorial = mode === 'bossPractice' ? null : new Tutorial(this.settings.tutorialDone, this.data.ships.ships[ship], this.input);
    this.tuning?.update();
    this.startRecorder(dock, ship);
    this.go(this.sim.control.mode === 'docked' && !this.scripted ? 'docked' : 'playing');
  }

  /** Événements de la simulation propres au cycle de jeu : début et fin de mission, arrivée à quai. */
  private onSimEvents(sim: Simulation, events: SimEvent[]): void {
    for (const e of events) {
      if (e.type === 'missionStarted') {
        this.meta = { scenario: e.scenario, ship: sim.state.weapons.shipId, startT: sim.time, saved: false };
        this.endedAt = null;
      } else if (e.type === 'missionEnded') {
        this.finishMission();
        this.endedAt = sim.time;
      } else if ((e.type === 'dock' && e.phase === 'docked') || e.type === 'respawn') {
        this.dockArrival = !this.scripted;
      } else if (e.type === 'jumpDrive' && e.phase === 'ready' && e.target) {
        // Exécuté au début de l'image suivante, hors de la boucle des ticks.
        this.pendingJump = e.target;
      }
    }
  }

  /** Clôt la mission en cours (une seule fois) : crédits, entrée de télémétrie, point de contrôle. */
  private finishMission(): void {
    const sim = this.sim, meta = this.meta;
    if (!sim || !meta || meta.saved) return;
    meta.saved = true;
    const summary = sim.state.telemetry.summary();
    this.lastReward = null;
    // Crédits de la mission (hors pilote automatique et missions sans une seconde de jeu).
    if (summary.simSeconds >= 1 && !this.autopilot) {
      this.lastReward = awardSortie(this.profile, this.data, meta.scenario, summary.outcome, summary.score);
      saveProfile(this.profile);
    }
    this.lastMission = {
      scenario: meta.scenario,
      ship: meta.ship,
      loadout: sim.loadout,
      startT: Math.round(meta.startT * 1000) / 1000,
      endT: Math.round(sim.time * 1000) / 1000,
      outcome: summary.outcome,
      credits: this.lastReward?.total ?? 0,
      summary,
    };
    this.recorder?.addMission(this.lastMission);
    this.checkpoint();
  }

  /** Une mission en cours est abandonnée (quitter, fermer l'onglet) et enregistrée. */
  private abandon(): void {
    if (this.sim?.mission.active) this.sim.abandonMission();
    this.finishMission();
  }

  // --- Télémétrie de partie ---------------------------------------------------

  private startRecorder(dock: DockId, ship: ShipId): void {
    const startedAt = new Date().toISOString();
    this.sessionStart = performance.now();
    this.checkpointTimer = CHECKPOINT_INTERVAL;
    this.recorder = new SessionRecorder({
      schema: SESSION_SCHEMA,
      sessionId: `${startedAt}-${this.seed}`,
      startedAt,
      updatedAt: startedAt,
      player: this.settings.playerName || 'anonyme',
      version: __BUILD_INFO__.version,
      buildCommit: __BUILD_INFO__.commit,
      buildDirty: __BUILD_INFO__.dirty,
      buildDate: __BUILD_INFO__.date,
      dataHash: dataHash(this.data),
      seed: this.seed,
      mode: 'world',
      startDock: dock,
      ship,
      mouseMode: this.settings.mouseMode,
      device: 'none',
      scripted: this.scripted,
      autopilot: !!this.autopilot,
      settings: { ...this.settings },
      machine: machineInfo(this.canvas, `Babylon.js ${Engine.Version}`),
      resolution: `${this.engine.getRenderWidth()}x${this.engine.getRenderHeight()}`,
      fps: { average: 0, p05: 0, samples: 0 },
      realSeconds: 0,
    });
    for (const e of this.pendingErrors.splice(0)) this.recorder.error(e.message, e.detail);
  }

  /** Envoie l'état actuel de la partie (même identifiant : le serveur garde le plus récent). */
  private checkpoint(outcome: GameSessionSummary['outcome'] = 'ongoing'): void {
    const recorder = this.recorder;
    if (!recorder) return;
    this.checkpointTimer = CHECKPOINT_INTERVAL;
    const record = recorder.toRecord(outcome, {
      realSeconds: (performance.now() - this.sessionStart) / 1000,
      fps: this.fps.stats(),
      device: this.autopilot ? 'autopilot' : this.input.device(),
      balance: this.profile.credits,
    });
    void saveSession(record, this.settings.shareTelemetry);
  }

  /** Fin de partie : mission en cours abandonnée, dernier envoi. */
  private closeSession(outcome: 'quit' | 'closed'): void {
    if (!this.recorder) return;
    this.abandon();
    this.checkpoint(outcome);
    this.recorder = null;
  }

  private recordAppEvent(type: string, data: Record<string, unknown> = {}): void {
    this.recorder?.appEvent(type, data);
  }

  /** Distribue les événements en attente (actions de menu hors tick, comme l'abandon depuis un dock). */
  private pumpEvents(sim: Simulation): void {
    const events = sim.state.drainEvents();
    this.renderer.handleEvents(events);
    this.hud.handleEvents(sim, events);
    this.audio.handleEvents(sim, events);
    this.onSimEvents(sim, events);
  }

  // --- Boucle --------------------------------------------------------------

  private frame(): void {
    const dt = Math.min(this.engine.getDeltaTime() / 1000, 0.25);
    this.input.pollGamepad();
    if (this.inMenuScene()) {
      this.updateMenuCamera(dt);
      this.hud.clear();
      this.renderer.environment.update(dt, this.menuCamera.position, Vector3.Zero());
      this.renderer.vfx.update(dt, () => null);
      this.renderer.render();
      return;
    }
    const sim = this.sim;
    if (!sim) {
      this.renderer.render();
      return;
    }
    if (this.pendingJump) {
      const zone = this.pendingJump;
      this.pendingJump = null;
      this.fighterJump(zone);
    }
    if (this.state === 'jumping') {
      this.renderer.updateJump(dt);
      this.hud.clear();
      this.renderer.render();
      return;
    }
    let alpha = 1;
    // À quai, le monde continue : service, ennemis, intermissions.
    const live = this.state === 'playing' || this.state === 'docked';
    if (live) {
      if (this.state === 'playing') this.fps.add(dt);
      if (this.hitStop > 0) this.hitStop -= dt;
      else this.accumulator += dt;
      let ticks = 0;
      while (this.accumulator >= TICK_DT && ticks < MAX_TICKS_PER_FRAME && this.hitStop <= 0) {
        this.renderer.capturePrevious();
        const input = this.state === 'docked' ? emptyInput() : this.autopilot ? this.autopilot.input(sim) : this.input.sample(ticks === 0);
        this.recorder?.onTick(sim.time, TICK_DT, sim.control.mode, input, this.input.lastDevice);
        sim.tick(input);
        const events = sim.state.drainEvents();
        if (this.recorder) {
          this.recorder.onEvents(events);
          this.recorder.sample(sim.time, () => ({
            position: sim.player.position.toArray(),
            speed: sim.player.velocity.length(),
            health: sim.player.health,
            maxHealth: sim.player.maxHealth,
            shield: sim.player.shield,
            throttle: sim.pilot.throttle,
            mode: sim.control.mode,
            enemies: sim.state.liveEnemies().length,
            mission: sim.mission.active ? `${sim.mission.scenarioId}:${sim.mission.phase}` : sim.mission.phase,
            fps: this.fps.current(),
          }));
        }
        this.renderer.handleEvents(events);
        this.hud.handleEvents(sim, events);
        this.audio.handleEvents(sim, events);
        this.feel(sim, events);
        this.onSimEvents(sim, events);
        if (this.state === 'playing' && this.tutorial && this.tutorial.observe(input, events, TICK_DT)) this.completeTutorial();
        this.accumulator -= TICK_DT;
        ticks++;
      }
      if (ticks === MAX_TICKS_PER_FRAME) this.accumulator = 0;
      alpha = this.accumulator / TICK_DT;
      if (this.endedAt !== null && sim.time - this.endedAt > RESULTS_DELAY && this.lastMission) {
        this.endedAt = null;
        this.go('results');
      } else if (this.dockArrival && this.state === 'playing' && sim.control.mode === 'docked') {
        this.dockArrival = false;
        this.dockMessage = '';
        this.go('docked');
      }
      if (this.state === 'docked') {
        this.statusTimer -= dt;
        if (this.statusTimer <= 0) {
          this.statusTimer = 0.2;
          this.menus.setDockStatus(this.serviceText(sim));
        }
      }
      if (this.state === 'playing') {
        // Idempotent : démarre la boucle moteur dès que l'audio est débloqué et chargé.
        this.audio.startEngine();
        this.audio.updateEngine(sim);
      }
    }
    if (this.recorder) {
      if (this.state === 'paused') this.recorder.addPausedSeconds(dt);
      this.checkpointTimer -= dt;
      if (this.checkpointTimer <= 0) this.checkpoint();
    }
    this.renderer.update(alpha, live ? dt : 0);
    // À quai, menus ouverts : caméra de dock qui cadre l'appareil à droite du panneau.
    const dockCam = sim.control.mode === 'docked' && this.state !== 'playing' && this.state !== 'paused';
    this.renderer.scene.activeCamera = dockCam ? this.menuCamera : this.renderer.rig.camera;
    if (dockCam) this.placeDockCamera(sim, dt);
    const cam = this.renderer.rig.camera;
    const q = cam.rotationQuaternion!;
    const basis = (x: number, y: number, z: number) => {
      const r = new Vector3(x, y, z).rotateByQuaternionToRef(q, new Vector3());
      return new Vec3(r.x, r.y, r.z);
    };
    this.hud.cameraBasis = { right: basis(1, 0, 0), up: basis(0, 1, 0), forward: basis(0, 0, -1) };
    this.hud.tutorialText = this.state === 'playing' && this.tutorial ? this.tutorial.text(this.input.device() === 'gamepad') : '';
    this.hud.debugText = this.debug ? this.debugText(sim) : '';
    if (this.state === 'playing' || this.state === 'paused') this.hud.draw(sim, cam, this.renderer.renderPositions, this.state === 'playing' ? dt : 0);
    else this.hud.clear();
    this.tuning?.update();
    this.renderer.render();
  }

  /** Ligne d'état du service à quai (menu du dock). */
  private serviceText(sim: Simulation): string {
    const player = sim.player;
    const w = sim.state.weapons;
    const parts = [`Coque ${Math.round(player.healthRatio() * 100)} %`];
    if (player.maxShield > 0) parts.push(`bouclier ${Math.round(player.shield)}/${player.maxShield}`);
    if (w.maxMissiles > 0) parts.push(`missiles ${w.missiles}/${w.maxMissiles}`);
    if (w.maxSwarm > 0) parts.push(`nuée ${w.swarm}/${w.maxSwarm}`);
    const full = player.health >= player.maxHealth && w.missiles >= w.maxMissiles && w.swarm >= w.maxSwarm;
    const mission = sim.mission.active ? ` · mission en cours : ${sim.mission.label} (${this.missionProgress(sim)})` : '';
    return `${full ? 'Service terminé' : 'Service en cours'} — ${parts.join(' · ')}${mission}`;
  }

  private missionProgress(sim: Simulation): string {
    const m = sim.mission;
    if (m.phase === 'starting') return `contact dans ${Math.max(0, Math.ceil(m.timer))} s`;
    if (m.phase === 'wave') return `vague ${m.waveIndex + 1}/${m.totalWaves}, ${m.enemiesRemaining} hostile(s)`;
    if (m.phase === 'intermission') return `prochaine rencontre dans ${Math.max(0, Math.ceil(m.timer))} s`;
    if (m.phase === 'boss') return 'croiseur ennemi engagé';
    if (m.phase === 'objective') return `${m.turrets.size}/${m.turretTotal} tourelle(s) debout`;
    return '';
  }

  /**
   * Nouvelle simulation dans une zone, même appareil et même partie (télémétrie continue, temps du monde
   * poursuivi). Saut du porteur : amarré au porteur ; saut de chasseur : en vol au point d'arrivée de la zone,
   * état du joueur conservé.
   */
  private enterZone(zone: string, from: Simulation, how: 'docked' | 'flight'): Simulation {
    const ship = from.state.weapons.shipId;
    const arrival = this.data.zones.zones[zone]!.arrival;
    const p = from.player, w = from.state.weapons;
    const next = new Simulation(this.data, {
      shipId: ship,
      mode: 'sandbox',
      seed: (from.state.seed * 31 + from.state.tickCount) % 2 ** 31,
      mouseMode: this.settings.mouseMode,
      startDocked: how === 'docked',
      startDock: 'carrier',
      autoLaunch: this.scripted,
      loadout: this.profile.loadouts[ship],
      zone,
      startTick: from.state.tickCount,
      ...(how === 'flight'
        ? {
            startPose: { position: arrival.position, rotation: arrival.rotation, speed: Math.max(40, Math.min(90, p.velocity.length())) },
            carry: { health: p.health, shield: p.shield, missiles: w.missiles, swarm: w.swarm, repairCharges: from.control.repairCharges },
          }
        : {}),
    }, this.collisionFor(zone));
    this.sim = next;
    this.meta = null;
    this.lastMission = null;
    this.endedAt = null;
    this.dockArrival = false;
    this.dockMessage = '';
    this.renderer.attach(next);
    this.hud.setData(next.state.data);
    this.hud.reset();
    this.accumulator = 0;
    return next;
  }

  /**
   * Saut du porteur vers une autre zone : vue extérieure, le porteur s'engouffre dans un portail, nouvelle
   * simulation dans la zone, puis il surgit d'un portail à son emplacement. Avec `scenario`, la mission est
   * choisie à l'arrivée et le décollage suit. Pas de saut pendant une mission.
   */
  private jumpTo(zone: string, scenario: string | null): void {
    const sim = this.sim;
    if (!sim || sim.mission.active || this.state === 'jumping' || !jumpTargets(this.data, sim.zone, 'carrier').includes(zone)) return;
    const atmospheric = isAtmosphericJump(this.data, sim.zone, zone);
    this.recordAppEvent('jump', { from: sim.zone, to: zone, scenario, by: 'carrier', atmospheric });
    this.go('jumping');
    this.renderer.hideShips();
    const carrier = this.renderer.carrierView();
    const { length, radius } = carrier.extent();
    const arrived = (next: Simulation) => {
      this.hud.notice(`${atmospheric ? (this.data.zones.zones[zone]!.kind === 'surface' ? 'DESCENTE TERMINÉE' : 'RETOUR EN ORBITE') : 'SAUT TERMINÉ'} — ${this.data.zones.zones[zone]!.label.toUpperCase()}`);
      this.go('docked');
      if (scenario && next.selectMission(scenario)) {
        this.recordAppEvent('missionAccepted', { scenario, dock: 'carrier', ship: next.state.weapons.shipId, loadout: next.loadout });
        this.launch();
      }
    };
    if (atmospheric) {
      // Descente vers la surface (piqué vers l'avant) ou remontée en orbite (cabré vers l'avant).
      const descending = this.data.zones.zones[zone]!.kind === 'surface';
      const forward = new Vector3(0, 0, -1).rotateByQuaternionToRef(carrier.root.rotationQuaternion!, new Vector3());
      const dir = forward.scale(descending ? 0.55 : 0.5).add(new Vector3(0, descending ? -0.83 : 0.86, 0)).normalize();
      const profile = carrierAtmosphereProfile(length, radius);
      this.renderer.jump.descend(carrier.root, profile, dir, () => {
        this.jumpFlash('fire');
        const next = this.enterZone(zone, sim, 'docked');
        carrier.resetPose();
        this.renderer.jump.emerge(carrier.root, profile, dir, () => arrived(next));
      });
      return;
    }
    const profile = carrierJumpProfile(length, radius);
    this.renderer.jump.depart(carrier.root, profile, () => {
      this.jumpFlash();
      const next = this.enterZone(zone, sim, 'docked');
      carrier.resetPose();
      this.renderer.jump.arrive(carrier.root, profile, () => {
        this.hud.notice(`SAUT TERMINÉ — ${this.data.zones.zones[zone]!.label.toUpperCase()}`);
        this.go('docked');
        if (scenario && next.selectMission(scenario)) {
          this.recordAppEvent('missionAccepted', { scenario, dock: 'carrier', ship: next.state.weapons.shipId, loadout: next.loadout });
          this.launch();
        }
      });
    });
  }

  /** Saut d'un chasseur équipé d'un module de saut (charge terminée dans la simulation). */
  private fighterJump(zone: string): void {
    const sim = this.sim;
    if (!sim || this.state !== 'playing' || sim.mission.active || !this.data.zones.zones[zone]) return;
    const root = this.renderer.playerRoot();
    if (!root) return;
    this.recordAppEvent('jump', { from: sim.zone, to: zone, scenario: null, by: 'fighter' });
    this.go('jumping');
    this.renderer.jump.depart(root, FIGHTER_JUMP, () => {
      this.jumpFlash();
      this.enterZone(zone, sim, 'flight');
      const arrival = this.renderer.playerRoot();
      const done = () => {
        this.hud.notice(`SAUT TERMINÉ — ${this.data.zones.zones[zone]!.label.toUpperCase()}`);
        this.renderer.rig.reset();
        this.go('playing');
      };
      if (arrival) this.renderer.jump.arrive(arrival, FIGHTER_JUMP, done);
      else done();
    });
  }

  /** Éclair plein écran du saut (bleu) ou de l'entrée atmosphérique (feu). */
  private jumpFlash(kind: 'portal' | 'fire' = 'portal'): void {
    let flash = document.getElementById('jump-flash');
    if (!flash) {
      flash = document.createElement('div');
      flash.id = 'jump-flash';
      flash.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:5;opacity:0;background:radial-gradient(circle at 50% 50%, rgba(235,245,255,0.95), rgba(120,170,255,0.7) 45%, rgba(20,30,60,0.9));transition:opacity 0.9s ease-out';
      document.body.appendChild(flash);
    }
    flash.style.background = kind === 'fire'
      ? 'radial-gradient(circle at 50% 50%, rgba(255,245,220,0.95), rgba(255,140,60,0.75) 45%, rgba(60,20,10,0.9))'
      : 'radial-gradient(circle at 50% 50%, rgba(235,245,255,0.95), rgba(120,170,255,0.7) 45%, rgba(20,30,60,0.9))';
    flash.style.transition = 'none';
    flash.style.opacity = this.settings.reducedFlashes ? '0.4' : '1';
    void flash.offsetWidth;
    flash.style.transition = 'opacity 0.9s ease-out';
    flash.style.opacity = '0';
  }

  /** Sensations : micro-pause sur destruction, secousses sur dégâts, explosions proches et boost. */
  private feel(sim: Simulation, events: SimEvent[]): void {
    const shake = this.data.camera.shake;
    const rig = this.renderer.rig;
    const player = sim.player;
    for (const e of events) {
      if (e.type === 'score') {
        this.hitStop = Math.max(this.hitStop, e.role === 'boss' ? this.data.art.hitStop * 3 : this.data.art.hitStop);
      } else if (e.type === 'damage' && e.targetKind === 'player' && e.applied > 0) {
        rig.shake(shake.damageAmplitude * Math.min(1.5, 0.3 + e.applied / 20), shake.damageDuration);
      } else if (e.type === 'damage' && e.targetKind === 'player' && e.absorbed > 0) {
        // Coup encaissé par le bouclier : secousse atténuée.
        rig.shake(shake.damageAmplitude * Math.min(0.6, 0.15 + e.absorbed / 40), shake.damageDuration * 0.7);
      } else if (e.type === 'explosion') {
        const d = Vec3.from(e.position).distanceTo(player.position);
        if (d < shake.explosionRange) rig.shake(shake.explosionAmplitude * (1 - d / shake.explosionRange), 0.3);
      } else if (e.type === 'playerDown') {
        rig.shake(shake.damageAmplitude * 2.5, 0.8);
      } else if (e.type === 'dock' && e.phase === 'launching') {
        rig.shake(shake.boostAmplitude * 2, 1.2);
      }
    }
    if (player.flight.boosting && !this.wasBoosting) rig.shake(shake.boostAmplitude, shake.boostDuration);
    this.wasBoosting = player.flight.boosting;
  }

  private debugText(sim: Simulation): string {
    const s = sim.state;
    const lines = [
      `${this.engine.getFps().toFixed(0)} i/s · t=${sim.time.toFixed(1)} s · graine ${sim.options.seed}`,
      `vaisseaux ${s.ships.length} · projectiles ${s.projectiles.length} · modules ${s.pickups.length} · effets ${this.renderer.vfx.activeCount} (refusés ${this.renderer.vfx.dropped})`,
      `vitesse ${sim.player.velocity.length().toFixed(1)} m/s · enveloppe ${sim.player.flight.envelope} · verrou ${s.weapons.lockState}`,
      `mission ${sim.mission.scenarioId ?? '—'} ${sim.mission.phase} · vague ${sim.mission.waveIndex + 1} · contrôle ${sim.control.mode} @ ${sim.control.dockId} · réserve ${sim.control.reserves}${this.autopilot ? ' · PILOTE AUTO' : ''}`,
    ];
    for (const brain of sim.brains.values()) {
      const d = brain.ship.position.distanceTo(sim.player.position);
      lines.push(`  ${brain.role.padEnd(11)} ${String(Math.round(d)).padStart(4)} m · ${brain.intent} · ${brain.ship.velocity.length().toFixed(0)} m/s`);
    }
    return lines.join('\n');
  }

  /** Galerie de dev : un exemplaire immobile de chaque rôle devant le joueur (lisibilité des silhouettes, W07). */
  private spawnGallery(sim: Simulation): void {
    const roles = ['interceptor', 'marksman', 'heavy', 'boss', 'drone'] as const;
    roles.forEach((role, i) => {
      const near = new URLSearchParams(location.search).get('gallery') === 'near';
      const distance = near ? (role === 'boss' ? 60 : 24) : role === 'boss' ? 120 : 34 + i * 10;
      const lateral = near ? (role === 'boss' ? 0 : (i - 1) * 15) : (i - 1.5) * 15;
      const position = role === 'drone'
        ? new Vec3(near ? -16 : -30, near ? -5 : -10, near ? -20 : -40)
        : new Vec3(lateral, near ? (role === 'boss' ? 26 : 9) : (i % 2) * 8 - 4, -distance);
      const ship = sim.spawn({ role, position, rotation: Quat.lookRotation(new Vec3(0.6, 0, 1)) });
      sim.brains.delete(ship.id);
      ship.velocity.set(0, 0, 0);
    });
  }

  private completeTutorial(): void {
    this.settings.tutorialDone = true;
    saveSettings(this.settings);
    this.hud.notice('TUTORIEL TERMINÉ');
  }

  private updateMenuCamera(dt: number): void {
    this.menuTime += dt;
    const a = this.menuTime * 0.035;
    this.menuCamera.position.set(Math.sin(a) * 640, 140 + Math.sin(a * 0.7) * 60, Math.cos(a) * 640);
    this.menuCamera.setTarget(new Vector3(60, -40, -120));
  }

  /** Dans le tunnel, derrière l'appareil, décalée pour le placer dans la moitié droite de l'écran. */
  private placeDockCamera(sim: Simulation, dt: number): void {
    this.menuTime += dt;
    const dock = sim.dock;
    const [px, py, pz] = dock.data.pad as [number, number, number];
    const sway = Math.sin(this.menuTime * 0.25) * 1.5;
    const pos = dock.toWorld([px - 10 + sway, py + 5, pz + 36]);
    const target = dock.toWorld([px - 10, py - 0.5, pz]);
    this.menuCamera.position.set(pos.x, pos.y, pos.z);
    this.menuCamera.setTarget(new Vector3(target.x, target.y, target.z));
  }

  private pause(): void {
    if (this.state === 'playing') this.go('paused');
  }

  // --- Menu du dock ----------------------------------------------------------

  private showDock(): void {
    const sim = this.sim;
    if (!sim) return;
    const dock = sim.control.dockId;
    if (!dockTabs[dock].includes(this.dockTab)) this.dockTab = 'missions';
    // Emplacement de saut absent du châssis courant (changement d'appareil) : retour à l'arme primaire.
    if (!slotAvailable(this.data, sim.state.weapons.shipId, this.armorySlot)) this.armorySlot = 'primary';
    const mission: DockMission = { active: sim.mission.active, label: sim.mission.label, progress: this.missionProgress(sim) };
    this.menus.show({ kind: 'dock', dock, zone: sim.zone, tab: this.dockTab, ship: sim.state.weapons.shipId, profile: this.profile, slot: this.armorySlot, message: this.dockMessage, mission });
    this.menus.setDockStatus(this.serviceText(sim));
  }

  private launch(): void {
    if (!this.sim || this.state !== 'docked') return;
    this.recordAppEvent('launchRequested', { dock: this.sim.control.dockId, mission: this.sim.mission.scenarioId, phase: this.sim.mission.phase });
    this.sim.requestLaunch();
    this.dockMessage = '';
    this.go('playing');
  }

  /** Change d'appareil (hangar de la station, hors mission). */
  private selectShip(ship: ShipId): void {
    const sim = this.sim;
    if (!sim || sim.control.dockId !== 'station') return;
    if (sim.mission.active) {
      this.dockMessage = 'Mission en cours : changement d’appareil impossible avant sa fin.';
    } else if (ship !== sim.state.weapons.shipId && sim.refit(ship, this.profile.loadouts[ship])) {
      this.recordAppEvent('refit', { ship, loadout: sim.loadout });
      this.settings.lastShip = ship;
      saveSettings(this.settings);
      this.renderer.refreshPlayer();
      this.hud.setData(sim.state.data);
      this.dockMessage = `${this.data.ships.ships[ship].label} prêt au décollage.`;
    }
    this.showDock();
  }

  /** Armurerie : monte un objet possédé, ou l'achète puis le monte (arsenal limité au porteur). */
  private armoryItem(id: string): void {
    const sim = this.sim;
    const item = this.data.equipment.items[id];
    if (!sim || !item) return;
    const ship = sim.state.weapons.shipId;
    const slot = this.armorySlot;
    const label = this.data.ships.ships[ship].label;
    const stocked = sim.control.dockId === 'station' || item.price === 0 || this.data.equipment.carrierStock.includes(id);
    if (this.profile.loadouts[ship][slot] === id) {
      this.dockMessage = `${item.label} est déjà monté.`;
    } else if (!stocked) {
      this.dockMessage = `${item.label} : disponible à la station seulement.`;
    } else if (owns(this.profile, this.data, id)) {
      if (equipItem(this.profile, this.data, ship, slot, id)) {
        this.dockMessage = `${item.label} monté sur le ${label}.`;
        this.recordAppEvent('equip', { item: id, slot, ship, dock: sim.control.dockId });
      }
    } else {
      const result = buyItem(this.profile, this.data, id);
      if (result === 'funds') {
        this.dockMessage = `Crédits insuffisants : ${item.price.toLocaleString('fr-FR')} ¢ requis.`;
        this.recordAppEvent('purchaseRefused', { item: id, price: item.price, credits: this.profile.credits });
      } else if (result === 'bought' && equipItem(this.profile, this.data, ship, slot, id)) {
        this.dockMessage = `${item.label} acheté (−${item.price.toLocaleString('fr-FR')} ¢) et monté sur le ${label}.`;
        this.recordAppEvent('purchase', { item: id, price: item.price, slot, ship, dock: sim.control.dockId, credits: this.profile.credits });
        this.recordAppEvent('equip', { item: id, slot, ship, dock: sim.control.dockId });
      }
    }
    if (sim.refit(ship, this.profile.loadouts[ship])) this.hud.setData(sim.state.data);
    saveProfile(this.profile);
    this.showDock();
  }

  // --- Actions -------------------------------------------------------------

  private onUi(action: UiAction): void {
    if (this.state === 'playing') {
      switch (action) {
        case 'pause':
        case 'menuBack':
          this.pause();
          break;
        case 'skipTutorial':
          this.tutorial?.skip();
          if (this.tutorial) this.completeTutorial();
          break;
        case 'debug':
          this.debug = !this.debug;
          break;
        case 'autopilot':
          if (import.meta.env.DEV) this.autopilot = this.autopilot ? null : new BotPilot();
          break;
        case 'tuning':
          void this.toggleTuning();
          break;
        default:
          break;
      }
      return;
    }
    if (this.state === 'jumping') {
      if (action === 'menuConfirm' || action === 'menuBack' || action === 'pause') this.renderer.jump.skip();
      return;
    }
    if (action === 'tuning') { void this.toggleTuning(); return; }
    if (action === 'debug') { this.debug = !this.debug; return; }
    if (this.state === 'paused' && (action === 'pause' || action === 'menuBack')) { this.go('playing'); return; }
    switch (action) {
      case 'menuUp': this.menus.move(-1); break;
      case 'menuDown': this.menus.move(1); break;
      case 'menuLeft': this.menus.adjust(-1); break;
      case 'menuRight': this.menus.adjust(1); break;
      case 'menuConfirm': this.menus.confirm(); break;
      case 'menuBack': this.onMenuAction('back'); break;
      default: break;
    }
  }

  private onMenuAction(action: MenuAction, value?: string): void {
    this.audio.ui();
    const sim = this.sim;
    switch (action) {
      case 'startDock':
        this.scripted = false;
        this.startWorld(value === 'carrier' ? 'carrier' : 'station');
        break;
      case 'changePilot': this.go('pilot'); break;
      case 'setPilot': {
        const name = (value ?? '').trim().replace(/\s+/g, ' ').slice(0, 32);
        if (!name) break;
        this.settings.playerName = name;
        saveSettings(this.settings);
        this.go('title');
        break;
      }
      case 'report': this.go('report'); break;
      case 'sendReport': {
        const text = (value ?? '').trim();
        if (!text || !sim) break;
        this.recorder?.bugReport(text, {
          state: this.returnTo,
          control: sim.control.mode,
          dock: sim.control.dockId,
          mission: sim.mission.scenarioId,
          missionPhase: sim.mission.phase,
          wave: sim.mission.waveIndex + 1,
          position: sim.player.position.toArray().map((v) => Math.round(v)),
          health: Math.round(sim.player.health),
          shield: Math.round(sim.player.shield),
          enemies: sim.state.liveEnemies().length,
          ship: sim.state.weapons.shipId,
          loadout: sim.loadout,
          fps: Math.round(this.engine.getFps()),
        });
        this.checkpoint();
        this.dockMessage = 'Merci : signalement envoyé avec le journal de la partie.';
        this.hud.notice('SIGNALEMENT ENVOYÉ');
        this.go(this.returnTo === 'docked' ? 'docked' : 'paused');
        break;
      }
      case 'dockTab':
        if (sim && value && dockTabs[sim.control.dockId].includes(value as DockTab)) this.dockTab = value as DockTab;
        this.dockMessage = '';
        this.showDock();
        break;
      case 'cycleTab': {
        if (!sim) break;
        const tabs = dockTabs[sim.control.dockId];
        const i = tabs.indexOf(this.dockTab);
        this.dockTab = tabs[(i + Number(value ?? 1) + tabs.length) % tabs.length]!;
        this.dockMessage = '';
        this.showDock();
        break;
      }
      case 'pickShip':
        if (value && shipIds.includes(value as ShipId)) this.selectShip(value as ShipId);
        break;
      case 'cycleShip': {
        if (!sim) break;
        const i = shipIds.indexOf(sim.state.weapons.shipId);
        this.selectShip(shipIds[(i + Number(value ?? 1) + shipIds.length) % shipIds.length]!);
        break;
      }
      case 'armorySlot':
        if (value && slotIds.includes(value as SlotId)) this.armorySlot = value as SlotId;
        this.dockMessage = '';
        this.showDock();
        break;
      case 'armoryItem': if (value) this.armoryItem(value); break;
      case 'acceptMission': {
        if (!sim || !value) break;
        const scenario = this.data.missions.scenarios[value];
        if (!scenario?.docks.includes(sim.control.dockId)) {
          this.dockMessage = 'Mission non proposée à ce dock.';
          this.showDock();
        } else if (scenario.zone !== sim.zone && !sim.mission.active) {
          // Mission d'une autre zone : le porteur saute d'abord (la surface ne se rejoint que depuis son orbite).
          if (sim.control.dockId !== 'carrier') {
            this.dockMessage = 'Mission d’une autre zone : le saut se fait depuis le porteur.';
            this.showDock();
          } else if (!jumpTargets(this.data, sim.zone, 'carrier').includes(scenario.zone)) {
            const orbit = this.data.zones.zones[scenario.zone]?.orbitOf;
            this.dockMessage = `Mission au sol : rejoignez d’abord ${this.data.zones.zones[orbit ?? '']?.label ?? 'l’orbite'}, puis descendez.`;
            this.showDock();
          } else {
            this.jumpTo(scenario.zone, value);
          }
        } else if (!sim.selectMission(value)) {
          this.dockMessage = 'Une mission est déjà en cours : terminez-la ou abandonnez-la.';
          this.recordAppEvent('missionRefused', { scenario: value, active: sim.mission.scenarioId });
          this.showDock();
        } else {
          this.recordAppEvent('missionAccepted', { scenario: value, dock: sim.control.dockId, ship: sim.state.weapons.shipId, loadout: sim.loadout });
          this.launch();
        }
        break;
      }
      case 'jump':
        if (!sim || !value || sim.control.dockId !== 'carrier' || this.state !== 'docked') break;
        if (sim.mission.active) {
          this.dockMessage = 'Mission en cours : terminez-la ou abandonnez-la avant le saut.';
          this.showDock();
        } else {
          this.jumpTo(value, null);
        }
        break;
      case 'abandonMission':
        if (!sim?.mission.active) break;
        this.recordAppEvent('missionAbandoned', { scenario: sim.mission.scenarioId, dock: sim.control.dockId });
        sim.abandonMission();
        this.pumpEvents(sim);
        this.endedAt = null;
        if (this.lastMission) this.go('results');
        break;
      case 'launch': this.launch(); break;
      case 'continue':
        this.lastMission = null;
        if (sim) this.go(sim.control.mode === 'docked' && !this.scripted ? 'docked' : 'playing');
        break;
      case 'options': this.go('options'); break;
      case 'help': this.go('help'); break;
      case 'resume': this.go('playing'); break;
      case 'quit': this.go('confirmQuit'); break;
      case 'confirmQuit': this.endToTitle(); break;
      case 'exportLast':
        if (this.recorder) {
          const record = this.recorder.toRecord('ongoing', { realSeconds: (performance.now() - this.sessionStart) / 1000, fps: this.fps.stats(), device: this.input.device(), balance: this.profile.credits });
          download(`space-arena-${record.header.startedAt.replace(/[:.]/g, '-')}.jsonl`, toJsonl(record));
        }
        break;
      case 'exportTelemetry':
        void listSessions().then((all) => download('space-arena-sessions.jsonl', all.map(toJsonl).join('')));
        break;
      case 'replayTutorial':
        this.settings.tutorialDone = false;
        saveSettings(this.settings);
        this.hud.notice('LE TUTORIEL REPRENDRA À LA PROCHAINE PARTIE');
        break;
      case 'setting': if (value) this.applySetting(value); break;
      case 'back':
        if (this.state === 'options' || this.state === 'help' || this.state === 'confirmQuit' || this.state === 'report') {
          this.go(this.returnTo === 'paused' || this.returnTo === 'docked' ? this.returnTo : 'title');
        } else if (this.state === 'pilot' && this.settings.playerName) {
          this.go('title');
        }
        break;
      default: break;
    }
  }

  /** Quitter vers le titre : la mission en cours est abandonnée, la partie est close, le profil est conservé. */
  private endToTitle(): void {
    this.closeSession('quit');
    this.renderer.jump.clear();
    this.renderer.carrierView().resetPose();
    this.renderer.detach();
    this.sim = null;
    this.renderer.setZone(this.data.zones.home);
    this.meta = null;
    this.hud.setData(this.data);
    this.go('title');
  }

  private applySetting(assignment: string): void {
    const [key, raw] = assignment.split('=') as [keyof Settings, string];
    const s = this.settings as unknown as Record<string, unknown>;
    const current = s[key];
    s[key] = typeof current === 'boolean' ? raw === 'true' : typeof current === 'number' ? Number(raw) : raw;
    saveSettings(this.settings);
    this.recordAppEvent('setting', { key, value: s[key] });
    this.input.setSettings(this.settings);
    this.hud.setSettings(this.settings);
    this.renderer.applySettings(this.settings);
    this.audio.configure(this.settings.sound, this.settings.volume);
    if (this.sim) this.sim.pilot.mouseMode = this.settings.mouseMode;
    if (this.state === 'options') {
      // Rafraîchit les libellés de valeurs sans perdre le focus.
      this.menus.show({ kind: 'options', settings: this.settings });
    }
  }

  private async toggleTuning(): Promise<void> {
    if (!import.meta.env.DEV) return;
    if (this.tuning) {
      this.tuning.dispose();
      this.tuning = null;
      return;
    }
    const { createTuningPanel } = await import('./dev/tuning');
    this.tuning = createTuningPanel({
      data: () => this.data,
      sim: () => this.sim,
      onChange: () => {
        this.renderer.setData(this.data);
        this.hud.setData(this.sim?.state.data ?? this.data);
      },
    });
  }

  /** Accès de test (Playwright) : état sans exposer la simulation au jeu. */
  snapshot() {
    return {
      state: this.state,
      time: this.sim?.time ?? 0,
      phase: this.sim?.mission.phase ?? null,
      wave: this.sim ? this.sim.mission.waveIndex + 1 : 0,
      enemies: this.sim?.state.liveEnemies().length ?? 0,
      vfxDropped: this.renderer?.vfx.dropped ?? 0,
      fps: this.engine?.getFps() ?? 0,
      credits: this.profile?.credits ?? 0,
      dock: this.sim?.control.dockId ?? null,
      control: this.sim?.control.mode ?? null,
    };
  }
}
